import { useId, useRef, type ReactNode, type PointerEvent } from 'react';
import { X } from 'lucide-react';
import { useModalDialog } from './useModalDialog';
import { cn } from '../lib/cn';

export type SheetProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** On ≥ md screens the sheet docks to the right instead of the bottom. */
  side?: 'auto' | 'bottom';
};

/**
 * Bottom sheet on phones, right-side panel on wide screens (§11b). Native <dialog>;
 * swipe down on the handle/header closes it, and a visible Close button always exists.
 */
export function Sheet({ open, onClose, title, description, children, footer, side = 'auto' }: SheetProps) {
  const ref = useModalDialog(open, onClose);
  const titleId = useId();
  const descId = useId();
  const drag = useRef<{ y: number; dy: number; id: number } | null>(null);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse') return;
    drag.current = { y: e.clientY, dy: 0, id: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const dlg = ref.current;
    if (!d || !dlg || d.id !== e.pointerId) return;
    d.dy = Math.max(0, e.clientY - d.y);
    dlg.style.transform = `translateY(${d.dy}px)`;
  };
  const onPointerUp = () => {
    const d = drag.current;
    const dlg = ref.current;
    drag.current = null;
    if (!d || !dlg) return;
    dlg.style.transform = '';
    if (d.dy > 80) onClose();
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={cn(
        'wd-sheet fixed m-0 w-full max-w-none border-border bg-card p-0 text-ink shadow-[0_-10px_30px_rgb(20_22_27/0.18)]',
        'inset-x-0 top-auto bottom-0 max-h-[92dvh] rounded-t-2xl border-t',
        side === 'auto' &&
          'md:inset-y-0 md:right-0 md:left-auto md:top-0 md:h-dvh md:max-h-none md:w-[420px] md:rounded-none md:border-t-0 md:border-l',
      )}
    >
      {open && (
        <div className={cn('flex max-h-[92dvh] flex-col', side === 'auto' && 'md:h-full md:max-h-none')}>
          <div
            className="touch-none select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div aria-hidden className={cn('mx-auto mt-2 h-1 w-10 rounded-full bg-border-strong', side === 'auto' && 'md:hidden')} />
            <header className="flex items-start gap-3 px-5 pt-3 pb-3">
              <div className="min-w-0 flex-1">
                <h2 id={titleId} className="text-[17px] font-semibold outline-none">
                  {title}
                </h2>
                {description && (
                  <p id={descId} className="mt-1 text-sm text-muted">
                    {description}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface hover:text-ink"
              >
                <X aria-hidden className="size-5" />
              </button>
            </header>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">{children}</div>
          {footer && (
            <footer className="flex flex-col gap-2 border-t border-divider px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}
